import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import type { Feed } from '~/services/layout/index.ts';
import { getFormScreenRuntime } from '~/services/plugins/quick-brick-form-screen/form-screen-state.ts';
import { DEFAULT_FORM_VALIDATION_MESSAGES } from '~/services/plugins/quick-brick-form-screen/form-validation-messages.ts';
import {
  isDateInputType,
  isNumberInputType,
} from '~/services/plugins/quick-brick-form-screen/types.ts';
import { getPluginConfig } from '~/utils/get-plugin-configuration.ts';
import { getComponentMarginsOnMaxWidth } from '~/utils/styles.ts';
import useCurrentLanguage from '~/hooks/use-current-language';
import manifest from '../config/manifest.ts';
import { DateModeInput } from './date-mode-input.tsx';
import {
  getAdditionInputStyle,
  getErrorLeadingAssetUrl,
  getRequiredLabelPosition,
  getResolvedInputType,
  getTextFieldEntryMetadata,
  getTitlePosition,
  pickNonEmptyString,
  type TextFieldUiComponent,
} from './helpers.ts';
import { TextModeInput } from './text-mode-input.tsx';
import {
  FieldCharacterCount,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldRequiredLabel,
  TextFieldLayout,
  type TextFieldLayoutSlot,
} from './text-field-layout.tsx';

const DEFAULT_REQUIRED_LABEL = '* Required';
const DEFAULT_OPEN_CALENDAR_LABEL = 'Open calendar';
const DEFAULT_SHOW_PASSWORD_LABEL = 'Show password';
const DEFAULT_HIDE_PASSWORD_LABEL = 'Hide password';

type QuickBrickComponentTextFieldProps = {
  uiComponent: TextFieldUiComponent;
  feed?: Feed;
};

export function QuickBrickComponentTextField({
  uiComponent,
  feed,
}: QuickBrickComponentTextFieldProps): JSX.Element {
  const fieldId =
    typeof uiComponent.data?.field_id === 'string'
      ? uiComponent.data.field_id
      : undefined;

  const language = useCurrentLanguage();
  const { styles, localizations } = getPluginConfig<typeof manifest>(
    manifest,
    uiComponent,
    false,
    language
  );
  const requiredLabel = pickNonEmptyString({
    value: localizations.required_label,
    fallback: DEFAULT_REQUIRED_LABEL,
  });
  const openCalendarLabel = pickNonEmptyString({
    value: localizations.open_calendar_label,
    fallback: DEFAULT_OPEN_CALENDAR_LABEL,
  });
  const showPasswordLabel = pickNonEmptyString({
    value: localizations.show_password_label,
    fallback: DEFAULT_SHOW_PASSWORD_LABEL,
  });
  const hidePasswordLabel = pickNonEmptyString({
    value: localizations.hide_password_label,
    fallback: DEFAULT_HIDE_PASSWORD_LABEL,
  });
  const metadata = useMemo(() => getTextFieldEntryMetadata(feed), [feed]);
  const inputType = useMemo(
    () => getResolvedInputType({ feed, uiComponent }),
    [feed, uiComponent]
  );
  const isDateMode = isDateInputType(inputType);
  const isNumberMode = isNumberInputType(inputType);
  const formValidationError =
    typeof uiComponent.data?.validation_error === 'string'
      ? uiComponent.data.validation_error
      : undefined;
  const [dateValidityError, setDateValidityError] = useState<
    string | undefined
  >();
  const validationError = formValidationError ?? dateValidityError;

  const runtime = getFormScreenRuntime();
  const invalidDateMessage =
    runtime?.validationMessages?.invalidDate ??
    DEFAULT_FORM_VALIDATION_MESSAGES.invalidDate;
  const storedValue =
    fieldId !== undefined ? runtime?.state.getValue(fieldId) ?? '' : '';

  const [value, setValue] = useState<string>(storedValue);

  useEffect(() => {
    setValue(storedValue);
  }, [storedValue, fieldId]);

  useEffect(() => {
    if (!fieldId || !runtime) return undefined;

    return runtime.state.subscribe(() => {
      setValue(runtime.state.getValue(fieldId) ?? '');
    });
  }, [fieldId, runtime]);

  if (!metadata || !fieldId) return <></>;

  const handleChange = (nextValue: string): void => {
    setValue(nextValue);
    getFormScreenRuntime()?.state.setValue(fieldId, nextValue);
  };

  const hasError = Boolean(validationError);
  const titleEnabled = styles.title_switch !== false;
  const titlePosition = getTitlePosition(styles.title_position);
  const errorEnabled = styles.error_switch !== false;
  const descriptionEnabled =
    styles.description_switch === true && Boolean(metadata.description);
  const characterCountEnabled =
    !isDateMode &&
    styles.character_count_switch === true &&
    metadata.maxLength !== undefined;
  const requiredLabelEnabled = styles.required_label_switch === true;
  const inputBorderStyle: string =
    typeof styles.input_border_style === 'string'
      ? styles.input_border_style
      : 'all_sides';
  const characterCountPosition =
    styles.character_count_position ?? 'below_input';
  const requiredLabelPosition = getRequiredLabelPosition(
    styles.required_label_position
  );
  const characterCountText =
    metadata.maxLength !== undefined
      ? `${value.length}/${metadata.maxLength}`
      : '';

  const inputId = `${uiComponent.id}-input`;
  const errorId = `${uiComponent.id}-error`;
  const descriptionId = `${uiComponent.id}-description`;
  const characterCountId = `${uiComponent.id}-character-count`;
  const additionInputStyle = getAdditionInputStyle(uiComponent.addition_styles);
  const showInlineCharacterCount =
    characterCountEnabled && characterCountPosition === 'text_input';

  const describedBy = [
    descriptionEnabled ? descriptionId : null,
    validationError && errorEnabled ? errorId : null,
    characterCountEnabled && !showInlineCharacterCount
      ? characterCountId
      : null,
  ]
    .filter(Boolean)
    .join(' ');

  const input = isDateMode ? (
    <DateModeInput
      inputId={inputId}
      describedBy={describedBy}
      hasError={hasError}
      required={Boolean(metadata.required)}
      inputBorderStyle={inputBorderStyle}
      additionInputStyle={additionInputStyle}
      storedValue={storedValue}
      invalidDateMessage={invalidDateMessage}
      openCalendarLabel={openCalendarLabel}
      datePickerAssets={{
        defaultAsset: styles.date_picker_button_default_asset,
        enableFocusedAsset: styles.date_picker_button_enable_focused_asset,
        focusedAsset: styles.date_picker_button_focused_asset,
        enableErrorAsset: styles.date_picker_button_enable_error_asset,
        errorAsset: styles.date_picker_button_error_asset,
      }}
      onReportedChange={handleChange}
      onValidityChange={setDateValidityError}
    />
  ) : (
    <TextModeInput
      inputId={inputId}
      describedBy={describedBy}
      hasError={hasError}
      required={metadata.required}
      isNumberMode={isNumberMode}
      value={value}
      placeholder={metadata.label}
      minLength={metadata.minLength}
      maxLength={metadata.maxLength}
      inputBorderStyle={inputBorderStyle}
      additionInputStyle={additionInputStyle}
      inlineCharacterCount={
        showInlineCharacterCount ? characterCountText : undefined
      }
      characterCountId={characterCountId}
      showPassword={{
        enabled: styles.show_password_button_switch === true,
        defaultHiddenAsset: styles.show_password_button_default_hidden_asset,
        enableHiddenHoverAsset:
          styles.show_password_button_enable_hidden_hover_asset,
        hoverHiddenAsset: styles.show_password_button_hover_hidden_asset,
        defaultVisibleAsset: styles.show_password_button_default_visible_asset,
        enableVisibleHoverAsset:
          styles.show_password_button_enable_visible_hover_asset,
        hoverVisibleAsset: styles.show_password_button_hover_visible_asset,
        showLabel: showPasswordLabel,
        hideLabel: hidePasswordLabel,
      }}
      onChange={handleChange}
    />
  );

  const slots: TextFieldLayoutSlot[] = [];

  if (requiredLabelEnabled && metadata.required) {
    slots.push({
      position: requiredLabelPosition,
      content: (
        <FieldRequiredLabel
          label={requiredLabel}
          alignment={styles.required_label_alignment}
          textTransform={styles.required_label_text_transform}
        />
      ),
    });
  }

  if (characterCountEnabled && !showInlineCharacterCount) {
    slots.push({
      position:
        characterCountPosition === 'above_input'
          ? 'above_input'
          : 'below_input',
      content: (
        <FieldCharacterCount
          id={characterCountId}
          text={characterCountText}
          hasError={hasError}
          alignment={styles.character_count_alignment}
        />
      ),
    });
  }

  return (
    <TextFieldLayout
      wrapperClassName={clsx(
        `component-${uiComponent.id}`,
        'component-maximum-width component-classes text-field-wrapper w-full',
        getComponentMarginsOnMaxWidth({ styles })
      )}
      titlePosition={titlePosition}
      titleBesideAlignment={
        styles.title_vertical_alignment === 'center'
          ? 'items-center'
          : 'items-start'
      }
      title={
        titleEnabled ? (
          <FieldLabel
            inputId={inputId}
            label={metadata.label}
            required={Boolean(metadata.required)}
            showAsterisk={!requiredLabelEnabled}
            titlePosition={titlePosition}
            alignment={styles.title_alignment}
            textTransform={styles.title_text_transform}
          />
        ) : null
      }
      description={
        descriptionEnabled && metadata.description ? (
          <FieldDescription
            id={descriptionId}
            description={metadata.description}
            alignment={styles.description_alignment}
            textTransform={styles.description_text_transform}
          />
        ) : null
      }
      input={input}
      slots={slots}
      error={
        validationError && errorEnabled ? (
          <FieldError
            id={errorId}
            message={validationError}
            alignment={styles.error_alignment}
            textTransform={styles.error_text_transform}
            display={styles.error_container_display}
            assetVerticalAlignment={
              styles.error_leading_asset_vertical_alignment
            }
            assetUrl={getErrorLeadingAssetUrl({
              enabled: styles.error_leading_asset_switch,
              asset: styles.error_leading_asset,
            })}
          />
        ) : null
      }
    />
  );
}

export default QuickBrickComponentTextField;
